import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsAndConditionsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#4B2D6E] text-white py-8">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/careers" className="inline-flex items-center text-white/80 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Careers
          </Link>
          <h1 className="text-3xl font-bold font-[Lato]">Terms and Conditions</h1>
          <p className="text-white/70 mt-2">SeniorPeerConnect™ — a product of JDI Business Solutions, LLC</p>
          <p className="text-white/60 text-sm mt-1">Last Updated: March 26, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8 text-[#333333] leading-relaxed">
        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">1. Acceptance of Terms</h2>
          <p>
            By accessing or using the SeniorPeerConnect™ website and applicant portal 
            (the "Service"), operated by JDI Business Solutions, LLC, you agree to be bound by these Terms and Conditions. If you do not agree to these terms, 
            please do not use the Service.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">2. Description of Service</h2>
          <p>
            The Service provides an online platform for individuals to explore employment opportunities, submit job applications, 
            complete preliminary assessments, and manage communications related to the hiring process. 
            SeniorPeerConnect™ is a product of JDI Business Solutions, LLC. The Service includes our careers page, application portal, 
            and related communication tools.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">3. SMS/Text Messaging Terms</h2>
          <p className="mb-3">
            By providing your mobile phone number and submitting an application, you expressly consent to receive 
            text messages from SeniorPeerConnect™ (operated by JDI Business Solutions, LLC) at the number provided. These messages may include:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Application status updates and confirmations</li>
            <li>Interview scheduling, reminders, and follow-ups</li>
            <li>Offer letter notifications</li>
            <li>Onboarding instructions and next steps</li>
            <li>Assessment progress reminders</li>
          </ul>
          <div className="mt-4 p-4 bg-[#F5F5F5] rounded-lg space-y-2">
            <p><strong>Program Name:</strong> SeniorPeerConnect™ Applicant Notifications</p>
            <p><strong>Message Frequency:</strong> Message frequency varies based on application activity. Typically 1-5 messages per week during active application periods.</p>
            <p><strong>Message & Data Rates:</strong> Message and data rates may apply. Check with your mobile carrier for details.</p>
            <p><strong>Opt-Out:</strong> You can opt out at any time by replying <strong>STOP</strong> to any message. You will receive a confirmation message and no further messages will be sent.</p>
            <p><strong>Help:</strong> For assistance, reply <strong>HELP</strong> to any message, or contact us at renee@shsnorthplainfield.com or (908) 830-1750.</p>
            <p><strong>Consent:</strong> Consent to receive SMS messages is not required as a condition of employment or application.</p>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">4. User Responsibilities</h2>
          <p className="mb-3">When using the Service, you agree to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide accurate, current, and complete information in your application</li>
            <li>Not impersonate any person or entity</li>
            <li>Not submit false, misleading, or fraudulent information</li>
            <li>Use the Service only for its intended purpose of employment applications</li>
            <li>Not attempt to gain unauthorized access to any portion of the Service</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">5. Intellectual Property</h2>
          <p>
            All content, features, and functionality of the Service, including but not limited to text, graphics, logos, 
            and software, are the property of JDI Business Solutions, LLC (d/b/a SeniorPeerConnect™) or its licensors and are 
            protected by copyright, trademark, and other intellectual property laws.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">6. Application Process</h2>
          <p className="mb-3">By submitting an application through the Service:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>You confirm that all information provided is truthful and accurate</li>
            <li>You consent to the processing of your personal data as described in our <Link to="/privacy-policy" className="text-[#4B2D6E] underline hover:text-[#E8722A]">Privacy Policy</Link></li>
            <li>You understand that submission of an application does not guarantee employment</li>
            <li>You authorize us to verify the information provided, including employment history and references</li>
            <li>You consent to background checks as part of the hiring process, where applicable</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">7. Limitation of Liability</h2>
          <p>
            The Service is provided "as is" without warranties of any kind. JDI Business Solutions, LLC 
            shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use 
            of the Service. We do not guarantee the accuracy, completeness, or timeliness of any information on the Service.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">8. Modifications</h2>
          <p>
            We reserve the right to modify these Terms and Conditions at any time. Changes will be effective upon posting to 
            the Service. Your continued use of the Service after any changes constitutes acceptance of the new terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">9. Governing Law</h2>
          <p>
            These Terms and Conditions shall be governed by and construed in accordance with the laws of the State of New Jersey, 
            without regard to its conflict of law provisions.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-bold text-[#4B2D6E] mb-3">10. Contact Us</h2>
          <p>If you have questions about these Terms and Conditions, please contact us:</p>
          <div className="mt-3 p-4 bg-[#F5F5F5] rounded-lg">
            <p className="font-semibold">JDI Business Solutions, LLC</p>
            <p className="text-sm text-[#666] mt-0.5">d/b/a SeniorPeerConnect™</p>
            <p className="text-sm text-[#666] mt-1">Email: renee@shsnorthplainfield.com</p>
            <p className="text-sm text-[#666]">Phone: (908) 830-1750</p>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div className="border-t py-6 text-center text-sm text-[#999]">
        <p>&copy; {new Date().getFullYear()} SeniorPeerConnect™, a product of JDI Business Solutions, LLC. All rights reserved.</p>
        <div className="mt-2 flex justify-center gap-4">
          <Link to="/privacy-policy" className="text-[#4B2D6E] hover:underline">Privacy Policy</Link>
          <Link to="/terms-and-conditions" className="text-[#4B2D6E] hover:underline">Terms & Conditions</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
